/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  deleteEnhanceSpeechTrack,
  EnhanceSpeechTrack,
  getEnhanceProcessingState,
} from "components/EnhancedSpeech/Utilities";
import { useDispatch } from "react-redux";
import { useCallback } from "react";
import { addToast } from "actions/ToastMessageActions";
import { EnhancementObject } from "components/EnhancedSpeech/useEnhanceSpeech";
import { UseEnhanceDispatch } from "components/EnhancedSpeech/useEnhanceSpeech/Reducer";

export const useBuildEnhancement = (
  dispatch: UseEnhanceDispatch
): ((track: EnhanceSpeechTrack) => EnhancementObject) => {
  const deleteTrack = useDeleteTrack(dispatch);

  return useCallback(
    (track: EnhanceSpeechTrack): EnhancementObject => ({
      track,
      processingState: getEnhanceProcessingState(track),
      isDeleting: false,
      delete: (): Promise<void> => deleteTrack(track),
      enhancedTrackName:
        track.trackName.split(".").slice(0, -1).join(".") +
        ` (${I18n.t("enhance_speech.export.file_postfix")}).wav`,
    }),
    [deleteTrack]
  );
};
const useDeleteTrack = (
  dispatchR: UseEnhanceDispatch
): ((track: EnhanceSpeechTrack) => Promise<void>) => {
  const globalDispatch = useDispatch();
  return useCallback(
    async (track: EnhanceSpeechTrack) => {
      dispatchR({
        type: "SET_ENHANCEMENT_DELETING",
        trackId: track.id,
      });

      const success = track.createdAt
        ? await deleteEnhanceSpeechTrack(track.id)
        : true;

      if (success) {
        dispatchR({
          type: "REMOVE_ENHANCEMENT",
          trackId: track.id,
        });
      } else {
        globalDispatch(
          addToast({
            message: I18n.t("enhance_speech.failed_to_delete"),
          })
        );
      }
      dispatchR({
        type: "UNSET_ENHANCEMENT_DELETING",
        trackId: track.id,
      });
    },
    [globalDispatch, dispatchR]
  );
};
